import { Button } from '@daily/shared/components/Button';
import { EllipsisIcon } from '@daily/shared/components/Icons/EllipsisIcon';
import { HelpIcon } from '@daily/shared/components/Icons/HelpIcon';
import { MicrophoneIcon } from '@daily/shared/components/Icons/MicrophoneIcon';
import { RaiseHandIcon } from '@daily/shared/components/Icons/RaiseHandIcon';
import { ScreenshareIcon } from '@daily/shared/components/Icons/ScreenshareIcon';
import { MenuButton } from '@daily/shared/components/Menu';
import { Text } from '@daily/shared/components/Text';
import { Tooltip } from '@daily/shared/components/Tooltip';
import { VisuallyHidden } from '@daily/shared/components/VisuallyHidden';
import {
  useLocalSessionId,
  useParticipantProperty,
} from '@daily-co/daily-react';
import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ParticipantMenu } from '/components/People';
import { useDisplayName } from '/hooks/useDisplayName';
import { useHasHandRaised } from '/hooks/useHandRaising';
import { useStyleVariants } from '/hooks/useStyleVariants';
import { useIsMobile } from '/lib/state/core';
import { useShowNames, useShowUserNameChangeUI } from '/lib/state/custom';
import { useNameModal } from '/lib/state/modals';

interface Props {
  iconVariant: ReturnType<typeof useStyleVariants>['iconVariant'];
  sessionId: string;
  showActions?: boolean;
  showMicMutedState: boolean;
  showHandRaising?: boolean;
  showIsAskingForHelp?: boolean;
  showIsScreenSharing: boolean;
}

export const ParticipantActions: React.FC<React.PropsWithChildren<Props>> =
  memo(
    ({
      iconVariant,
      sessionId,
      showActions = true,
      showHandRaising,
      showIsAskingForHelp = false,
      showIsScreenSharing,
      showMicMutedState,
    }) => {
      const { t } = useTranslation();
      const [, setShowNameModal] = useNameModal();
      const [isMobile] = useIsMobile();
      const [showNames] = useShowNames();
      const [showUserNameChangeUI] = useShowUserNameChangeUI();
      const [showMenu, setShowMenu] = useState(false);
      const handleChangeNameClick = () => {
        setShowNameModal(true);
        setShowMenu(false);
      };
      const handleMenuClick = () => setShowMenu(true);
      const handleMenuClose = () => setShowMenu(false);

      const localSessionId = useLocalSessionId();
      const isLocal = sessionId === localSessionId;
      const [userName, hasAudioEnabled] = useParticipantProperty(sessionId, [
        'user_name',
        'audio',
      ]);

      const displayName = useDisplayName(sessionId, showNames);

      const isHandRaised = useHasHandRaised(sessionId);
      const isLocalOwner = useParticipantProperty(localSessionId, 'owner');

      if (!sessionId) return null;

      if (isLocal && !userName && showUserNameChangeUI) {
        return (
          <Button
            id="change-name"
            onClick={handleChangeNameClick}
            variant="ghost"
            style={{ display: 'inline-flex' }}
          >
            <Text color="muted" underline>
              {t('people.enterYourName')}
            </Text>
          </Button>
        );
      }

      const btnId = `${sessionId}-actions-btn`;
      const menuId = `${sessionId}-actions`;

      // On mobile there are no options for non-owners on remote participants
      // On desktop there's at least always the possibility to pin/unpin a participant
      const shouldRenderMenu = isMobile ? isLocalOwner || isLocal : true;

      return (
        <div>
          {showIsAskingForHelp && (
            <Tooltip
              id={`tt-ask-for-help-${sessionId}`}
              title={t('breakouts.userAskingForHelp', {
                name: displayName,
              })}
            >
              <HelpIcon size={iconVariant} />
            </Tooltip>
          )}
          {showIsScreenSharing && <ScreenshareIcon size={iconVariant} />}
          {showHandRaising && isHandRaised && (
            <RaiseHandIcon size={iconVariant} />
          )}
          {showMicMutedState && (
            <MicrophoneIcon
              color={!hasAudioEnabled ? 'var(--text-error)' : 'currentColor'}
              muted={!hasAudioEnabled}
              size={iconVariant}
            />
          )}
          {showActions && shouldRenderMenu && (
            <MenuButton
              aria-controls={menuId}
              focusable
              id={btnId}
              onClick={handleMenuClick}
              open={showMenu}
              variant="ghost"
            >
              <EllipsisIcon size={iconVariant} />
              <VisuallyHidden>{t('people.showOptions')}</VisuallyHidden>
            </MenuButton>
          )}
          {showMenu && (
            <ParticipantMenu
              align="right"
              aria-labelledby={btnId}
              id={menuId}
              onClose={handleMenuClose}
              sessionId={sessionId}
            />
          )}
          <style jsx>{`
            div {
              display: flex;
              gap: 8px;
            }
          `}</style>
        </div>
      );
    }
  );
ParticipantActions.displayName = 'ParticipantActions';
